import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  Provider,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideTransloco, TRANSLOCO_CONFIG, translocoConfig } from '@jsverse/transloco';
import { I18Service } from '@translation';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { TranslocoHttpLoader } from '@translation/transloco-loader';
import { DesignSystemModule } from '@compeople-shared/design-system-angular';
import { defineCustomElements as designSystemDefineElements } from '@cp-aida/design-system-icons/loader';

import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { EnvironmentService } from '@services/environment.service';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { Configuration } from '@api';
import { errorInterceptor } from './interceptors/error.interceptor';

designSystemDefineElements();

const translocoConfigValues = {
  availableLangs: ['en-EN'],
  defaultLang: 'en-EN',
  fallbackLang: 'en-EN',
  reRenderOnLangChange: true,
  missingHandler: {
    allowEmpty: false,
    useFallbackTranslation: false,
    logMissingKey: true,
  },
  prodMode: false,
};

export const appConfig: ApplicationConfig = {
  providers: [
    EnvironmentService,
    I18Service,
    provideAppInitializer(() => {
      const env = inject(EnvironmentService);
      return env.init();
    }),
    {
      provide: Configuration,
      useFactory: apiConfigurationFactory,
    },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([errorInterceptor])),
    importProvidersFrom(DesignSystemModule),
    provideTransloco({
      config: { ...translocoConfigValues },
      loader: TranslocoHttpLoader,
    }),
    //Override prodMode based on environment
    {
      provide: TRANSLOCO_CONFIG,
      useFactory: (env: EnvironmentService) =>
        translocoConfig({
          ...translocoConfigValues,
          prodMode: env.isProd(),
        }),
      deps: [EnvironmentService],
    } as Provider,
    provideTranslocoLocale(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    MsalRedirectComponent,
  ],
};

export function apiConfigurationFactory(): Configuration {
  const environmentService = inject(EnvironmentService);
  return new Configuration({
    basePath: `${environmentService.profile.api}`,
    withCredentials: true,
  });
}

export function MSALInstanceFactory(): IPublicClientApplication {
  const environmentService = inject(EnvironmentService);
  return new PublicClientApplication({
    auth: {
      clientId: environmentService.profile.msalConfig.auth.clientId,
      authority: environmentService.profile.msalConfig.auth.authority,
      redirectUri: environmentService.profile.url,
      postLogoutRedirectUri: environmentService.profile.url,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false,
    },
  });
}

// MSAL Interceptor is required to request access tokens in order to access the protected resource
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const environmentService = inject(EnvironmentService);
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set(environmentService.profile.api, [
    `${environmentService.profile.msalConfig.auth.clientId}/aida.art.access`,
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

// MSAL Guard is required to protect routes and require authentication before accessing protected routes
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  const environmentService = inject(EnvironmentService);
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [`${environmentService.profile.msalConfig.auth.clientId}/aida.art.access`],
    },
  };
}
